import { useEffect, useRef, useState } from "react";
import { Camera, Upload, Loader2, CheckCircle2, Briefcase, UserCheck, Download, Printer, RotateCcw, FileText, ArrowRight, ArrowLeft, PenLine, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { canEmbedPdf } from "../lib/pdf";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { OwnerForm } from "./Owners";

const STEPS = ["Type de contrat", "Pièce d'identité", "Informations client", "Location", "Contrat généré"];

const EMPTY_FORM = {
  nom: "", prenom: "", adresse: "", date_naissance: "", age: "", profession: "",
  numero_piece: "", type_piece: "", telephone: "", email: "",
};
const todayFr = () => new Date().toLocaleDateString("fr-FR");
const EMPTY_TERMS = { designation: "", montant: "", caution: "", duree: "", duree_indeterminee: false, date_effet: todayFr(), resiliation: false };
const ID_TYPES = ["Carte d'identité nationale", "Carte NINA", "Passeport", "Permis de conduire", "Autre"];
const PROFESSIONS = ["Commerçant", "Entrepreneur", "Fonctionnaire", "Salarié", "Médecin", "Enseignant", "Étudiant", "Sans emploi", "Autre"];

function computeAge(dateStr) {
  const m = dateStr.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return "";
  const birth = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
  if (isNaN(birth.getTime())) return "";
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  if (now.getMonth() < birth.getMonth() || (now.getMonth() === birth.getMonth() && now.getDate() < birth.getDate())) age--;
  return age >= 0 && age < 130 ? String(age) : "";
}

function Stepper({ step }) {
  return (
    <ol className="flex items-center w-full mb-8" data-testid="wizard-stepper">
      {STEPS.map((label, i) => {
        const n = i + 1;
        const active = n === step;
        const done = n < step;
        return (
          <li key={label} className={`flex items-center ${n < STEPS.length ? "flex-1" : ""}`}>
            <div className="flex flex-col items-center gap-1.5">
              <span className={`flex items-center justify-center h-9 w-9 rounded-full text-sm font-bold border-2 transition-colors duration-200 ${
                done ? "bg-emerald-600 border-emerald-600 text-white"
                  : active ? "bg-slate-900 border-slate-900 text-white"
                  : "bg-white border-slate-300 text-slate-400"
              }`} data-testid={`wizard-step-${n}`}>
                {done ? <CheckCircle2 className="h-5 w-5" /> : n}
              </span>
              <span className={`text-xs font-medium hidden sm:block text-center ${active ? "text-slate-900" : "text-slate-400"}`}>{label}</span>
            </div>
            {n < STEPS.length && <div className={`flex-1 h-0.5 mx-2 sm:mx-3 ${done ? "bg-emerald-600" : "bg-slate-200"}`} />}
          </li>
        );
      })}
    </ol>
  );
}

export default function Generate() {
  const [step, setStep] = useState(1);
  const [type, setType] = useState(null);
  const [preview, setPreview] = useState(null);
  const [file, setFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [rawText, setRawText] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [terms, setTerms] = useState(EMPTY_TERMS);
  const [generating, setGenerating] = useState(false);
  const [contract, setContract] = useState(null);
  const [owners, setOwners] = useState([]);
  const [ownerId, setOwnerId] = useState("");
  const [ownerDialog, setOwnerDialog] = useState(false);
  const [ownerForm, setOwnerForm] = useState({ nom: "", prenom: "", adresse: "", telephone: "", profession: "", notes: "" });
  const [ownerSaving, setOwnerSaving] = useState(false);
  const fileInput = useRef(null);
  const cameraInput = useRef(null);

  const selectedOwner = owners.find((o) => o.owner_id === ownerId);

  useEffect(() => {
    api.get("/owners").then(({ data }) => setOwners(data)).catch(() => {});
  }, []);

  useEffect(() => () => {
    if (preview) URL.revokeObjectURL(preview);
  }, [preview]);

  const createOwner = async () => {
    setOwnerSaving(true);
    try {
      const { data } = await api.post("/owners", ownerForm);
      setOwners((prev) => [...prev, data].sort((a, b) => a.nom.localeCompare(b.nom)));
      setOwnerId(data.owner_id);
      setOwnerDialog(false);
      setOwnerForm({ nom: "", prenom: "", adresse: "", telephone: "", profession: "", notes: "" });
      toast.success("Propriétaire créé et sélectionné");
    } catch (e) {
      toast.error(formatApiError(e, "Création impossible"));
    } finally {
      setOwnerSaving(false);
    }
  };

  const fileUrl = (format) =>
    contract ? `${process.env.REACT_APP_BACKEND_URL}/api/contracts/${contract.contract_id}/file?format=${format}` : "#";

  const releasePreview = () => {
    if (preview) URL.revokeObjectURL(preview);
  };

  const handleFile = (f) => {
    if (!f) return;
    releasePreview();
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const analyze = async () => {
    if (!file) return;
    setAnalyzing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/ocr/extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((prev) => ({
        ...prev,
        nom: data.nom || prev.nom,
        prenom: data.prenom || prev.prenom,
        date_naissance: data.date_naissance || prev.date_naissance,
        numero_piece: data.numero_piece || prev.numero_piece,
        type_piece: data.type_piece || prev.type_piece,
        age: data.date_naissance ? computeAge(data.date_naissance) : prev.age,
      }));
      setRawText(data.raw_text || "");
      setStep(3);
      if (!data.nom && !data.prenom) {
        toast.info("Lecture automatique incertaine : veuillez saisir les informations manuellement.");
      } else {
        toast.success("Informations détectées. Veuillez vérifier.");
      }
    } catch (e) {
      toast.error(formatApiError(e, "Échec de l'analyse OCR"));
    } finally {
      setAnalyzing(false);
    }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/contracts/generate", { ...form, ...terms, type, owner_id: ownerId });
      setContract(data);
      setStep(5);
      toast.success(`Contrat ${type === "professionnel" ? "professionnel" : "personnel"} créé et enregistré avec succès (${data.numero_contrat}).`);
    } catch (e) {
      toast.error(formatApiError(e, "Échec de la génération du contrat"));
    } finally {
      setGenerating(false);
    }
  };

  const printContract = () => {
    const url = fileUrl("pdf");
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    iframe.src = url;
    const cleanup = () => {
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
    };
    iframe.onload = () => {
      try {
        iframe.contentWindow.onafterprint = cleanup;
        iframe.contentWindow.print();
      } catch {
        window.open(url, "_blank");
        cleanup();
      }
      setTimeout(cleanup, 60000);
    };
    document.body.appendChild(iframe);
  };

  const reset = () => {
    releasePreview();
    setStep(1); setType(null); setPreview(null); setFile(null);
    setForm(EMPTY_FORM); setTerms({ ...EMPTY_TERMS, date_effet: todayFr() });
    setRawText(""); setContract(null);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="generate-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Nouveau contrat</h1>
        <p className="text-sm text-slate-500 mt-1">Type de contrat → pièce d'identité → informations → génération automatique.</p>
      </div>

      <Stepper step={step} />

      {step === 1 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-10 shadow-sm" data-testid="wizard-step-type">
          <p className="text-center text-base font-semibold text-slate-900 mb-8">Quel type de contrat souhaitez-vous générer ?</p>
          <div className="grid sm:grid-cols-2 gap-5">
            <button onClick={() => setType("professionnel")} data-testid="wizard-type-pro-btn"
              className={`group border-2 rounded-2xl p-8 text-left transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 ${
                type === "professionnel" ? "border-blue-500 bg-blue-50/50 shadow-md" : "border-slate-200 hover:border-blue-500 bg-white"
              }`}>
              <span className={`p-3 rounded-xl inline-block mb-4 transition-colors duration-200 ${
                type === "professionnel" ? "bg-blue-600 text-white" : "bg-blue-50 text-blue-600 group-hover:bg-blue-600 group-hover:text-white"
              }`}>
                <Briefcase className="h-7 w-7" />
              </span>
              <p className="text-lg font-bold text-slate-900">CONTRAT PROFESSIONNEL</p>
              <p className="text-sm text-slate-500 mt-1">Bail à usage professionnel (modèle OHADA).</p>
            </button>
            <button onClick={() => setType("personnel")} data-testid="wizard-type-personal-btn"
              className={`group border-2 rounded-2xl p-8 text-left transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 ${
                type === "personnel" ? "border-emerald-500 bg-emerald-50/50 shadow-md" : "border-slate-200 hover:border-emerald-500 bg-white"
              }`}>
              <span className={`p-3 rounded-xl inline-block mb-4 transition-colors duration-200 ${
                type === "personnel" ? "bg-emerald-600 text-white" : "bg-emerald-50 text-emerald-600 group-hover:bg-emerald-600 group-hover:text-white"
              }`}>
                <UserCheck className="h-7 w-7" />
              </span>
              <p className="text-lg font-bold text-slate-900">CONTRAT PERSONNEL</p>
              <p className="text-sm text-slate-500 mt-1">Bail à usage d'habitation.</p>
            </button>
          </div>
          <div className="mt-8 flex justify-end">
            <Button onClick={() => setStep(2)} disabled={!type} data-testid="wizard-type-continue-btn">
              Continuer <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-10 shadow-sm" data-testid="wizard-step-upload">
          <input ref={fileInput} type="file" accept="image/jpeg,image/jpg,image/png" className="hidden"
            data-testid="wizard-file-input" onChange={(e) => handleFile(e.target.files[0])} />
          <input ref={cameraInput} type="file" accept="image/*" capture="environment" className="hidden"
            data-testid="wizard-camera-input" onChange={(e) => handleFile(e.target.files[0])} />

          {!preview ? (
            <>
              <div
                className="border-2 border-dashed border-slate-300 rounded-xl p-10 text-center hover:border-blue-400 hover:bg-blue-50/40 transition-colors duration-200 cursor-pointer"
                onClick={() => fileInput.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
                data-testid="wizard-dropzone"
              >
                <Upload className="h-10 w-10 text-slate-400 mx-auto mb-4" />
                <p className="text-base font-semibold text-slate-900">Importer la pièce d'identité</p>
                <p className="text-sm text-slate-500 mt-1">Glissez-déposez une image ou cliquez pour parcourir (JPG, JPEG, PNG)</p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
                  <Button type="button" onClick={(e) => { e.stopPropagation(); fileInput.current?.click(); }} data-testid="wizard-upload-btn">
                    <Upload className="h-4 w-4 mr-2" /> Importer un fichier
                  </Button>
                  <Button type="button" variant="outline" onClick={(e) => { e.stopPropagation(); cameraInput.current?.click(); }} data-testid="wizard-camera-button">
                    <Camera className="h-4 w-4 mr-2" /> Prendre une photo
                  </Button>
                </div>
              </div>
              <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-between">
                <Button variant="outline" onClick={() => setStep(1)} data-testid="wizard-back-btn">
                  <ArrowLeft className="h-4 w-4 mr-2" /> Retour
                </Button>
                <Button variant="ghost" onClick={() => setStep(3)} data-testid="wizard-manual-entry-btn">
                  <PenLine className="h-4 w-4 mr-2" /> Saisie manuelle sans photo
                </Button>
              </div>
            </>
          ) : (
            <div className="space-y-5">
              <div className="rounded-xl overflow-hidden border border-slate-200 bg-slate-100 flex justify-center">
                <img src={preview} alt="Pièce d'identité" className="max-h-96 object-contain" data-testid="wizard-id-preview" />
              </div>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button onClick={analyze} disabled={analyzing} data-testid="wizard-analyze-btn">
                  {analyzing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                  {analyzing ? "Analyse en cours…" : "Lire la pièce d'identité"}
                </Button>
                <Button variant="outline" onClick={() => { releasePreview(); setFile(null); setPreview(null); }} data-testid="wizard-reimport-btn">
                  <RotateCcw className="h-4 w-4 mr-2" /> Ré-importer
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {step === 3 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-10 shadow-sm space-y-6" data-testid="wizard-step-verify">
          <div className="flex items-center gap-2 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3">
            <CheckCircle2 className="h-5 w-5 shrink-0" />
            <span className="text-sm font-semibold">Vérifiez les informations détectées et complétez le formulaire client.</span>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label htmlFor="nom">Nom *</Label>
              <Input id="nom" value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })}
                placeholder="Ex : TRAORÉ" data-testid="wizard-input-lastname" className="text-base font-semibold" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="prenom">Prénom *</Label>
              <Input id="prenom" value={form.prenom} onChange={(e) => setForm({ ...form, prenom: e.target.value })}
                placeholder="Ex : Amadou" data-testid="wizard-input-firstname" className="text-base font-semibold" />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="adresse">Adresse complète</Label>
              <Input id="adresse" value={form.adresse} onChange={(e) => setForm({ ...form, adresse: e.target.value })}
                placeholder="Ex : Bamako-Coura, Rue 354, Porte 63" data-testid="wizard-input-address" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="date_naissance">Date de naissance</Label>
              <Input id="date_naissance" value={form.date_naissance} placeholder="JJ/MM/AAAA"
                onChange={(e) => {
                  const v = e.target.value;
                  setForm({ ...form, date_naissance: v, age: computeAge(v) });
                }}
                data-testid="wizard-input-birthdate" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="age">Âge</Label>
              <Input id="age" value={form.age} readOnly placeholder="Calculé automatiquement"
                data-testid="wizard-input-age" className="bg-slate-50 text-slate-600" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="profession">Profession</Label>
              <Input id="profession" list="professions-list" value={form.profession}
                onChange={(e) => setForm({ ...form, profession: e.target.value })}
                placeholder="Ex : Commerçant" data-testid="wizard-input-profession" />
              <datalist id="professions-list">
                {PROFESSIONS.map((p) => <option key={p} value={p} />)}
              </datalist>
            </div>
            <div className="space-y-2">
              <Label htmlFor="type_piece">Type de pièce d'identité</Label>
              <Select value={form.type_piece} onValueChange={(v) => setForm({ ...form, type_piece: v })}>
                <SelectTrigger id="type_piece" data-testid="wizard-input-id-type">
                  <SelectValue placeholder="Sélectionner…" />
                </SelectTrigger>
                <SelectContent>
                  {ID_TYPES.map((t) => (
                    <SelectItem key={t} value={t} data-testid={`wizard-id-type-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="numero_piece">Numéro de pièce d'identité</Label>
              <Input id="numero_piece" value={form.numero_piece} onChange={(e) => setForm({ ...form, numero_piece: e.target.value })}
                placeholder="Ex : 16006205001023Y" data-testid="wizard-input-id-number" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="telephone">Téléphone</Label>
              <Input id="telephone" type="tel" value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })}
                placeholder="Ex : 99 68 98 03" data-testid="wizard-input-phone" />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="email">Email (si disponible)</Label>
              <Input id="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="client@exemple.com" data-testid="wizard-input-email" />
            </div>
          </div>
          {rawText && (
            <details className="text-xs text-slate-500">
              <summary className="cursor-pointer font-medium" data-testid="wizard-raw-ocr-toggle">Voir le texte brut lu par l'OCR</summary>
              <pre className="mt-2 bg-slate-50 border border-slate-200 rounded-lg p-3 whitespace-pre-wrap max-h-40 overflow-auto" data-testid="wizard-raw-ocr">{rawText}</pre>
            </details>
          )}
          <div className="flex flex-col sm:flex-row gap-3 justify-between">
            <Button variant="outline" onClick={() => setStep(2)} data-testid="wizard-back-to-upload-btn">
              <ArrowLeft className="h-4 w-4 mr-2" /> Retour
            </Button>
            <Button onClick={() => setStep(4)} disabled={!form.nom.trim() || !form.prenom.trim()} data-testid="wizard-continue-btn">
              ✓ Informations vérifiées — Continuer <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      )}

      {step === 4 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-10 shadow-sm" data-testid="wizard-step-terms">
          <p className="text-center text-sm text-slate-500 mb-1">
            {type === "professionnel" ? "Contrat professionnel" : "Contrat personnel"} pour
          </p>
          <p className="text-center text-xl font-bold text-slate-900 mb-8" data-testid="wizard-client-name">
            {form.prenom} {form.nom.toUpperCase()}
          </p>

          <div className="max-w-xl mx-auto space-y-4">
            <div className="bg-blue-50/60 border border-blue-100 rounded-xl p-4 space-y-3">
              <p className="text-xs font-medium text-blue-700 uppercase tracking-wider">Informations du propriétaire *</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1">
                  <Select value={ownerId} onValueChange={setOwnerId}>
                    <SelectTrigger className="bg-white" data-testid="wizard-owner-select">
                      <SelectValue placeholder="Sélectionner un propriétaire…" />
                    </SelectTrigger>
                    <SelectContent>
                      {owners.map((o) => (
                        <SelectItem key={o.owner_id} value={o.owner_id} data-testid={`wizard-owner-option-${o.owner_id}`}>
                          {o.prenom} {o.nom.toUpperCase()}{o.adresse ? ` — ${o.adresse}` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button type="button" variant="outline" onClick={() => setOwnerDialog(true)} data-testid="wizard-owner-add-btn">
                  <Plus className="h-4 w-4 mr-2" /> Nouveau propriétaire
                </Button>
              </div>
              {selectedOwner && (
                <p className="text-xs text-slate-600" data-testid="wizard-owner-summary">
                  {[selectedOwner.adresse, selectedOwner.telephone, selectedOwner.profession].filter(Boolean).join(" · ") || "Aucune information complémentaire"}
                </p>
              )}
              {owners.length === 0 && (
                <p className="text-xs text-amber-700">Aucun propriétaire enregistré — créez-en un avec le bouton ci-dessus.</p>
              )}
            </div>

            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Informations de location</p>
            <div className="space-y-2">
              <Label htmlFor="designation">Bien loué (désignation)</Label>
              <Input id="designation" value={terms.designation} onChange={(e) => setTerms({ ...terms, designation: e.target.value })}
                placeholder="Ex : Un magasin sis à Djicoroni-Para" data-testid="wizard-input-designation" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="montant">Montant du loyer (FCFA)</Label>
                <Input id="montant" value={terms.montant} onChange={(e) => setTerms({ ...terms, montant: e.target.value })}
                  placeholder="Ex : 20 000" data-testid="wizard-input-amount" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="caution">Caution (FCFA)</Label>
                <Input id="caution" value={terms.caution} onChange={(e) => setTerms({ ...terms, caution: e.target.value })}
                  placeholder="Par défaut : 2 mois de loyer" data-testid="wizard-input-caution" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="duree">Durée de location</Label>
                <Input id="duree" value={terms.duree} disabled={terms.duree_indeterminee}
                  onChange={(e) => setTerms({ ...terms, duree: e.target.value })}
                  placeholder={terms.duree_indeterminee ? "Indéterminée" : "Ex : 1 an"} data-testid="wizard-input-duration" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="date_effet">Date d'effet</Label>
                <Input id="date_effet" value={terms.date_effet} onChange={(e) => setTerms({ ...terms, date_effet: e.target.value })}
                  placeholder="JJ/MM/AAAA" data-testid="wizard-input-date-effet" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="duree_indeterminee" checked={terms.duree_indeterminee}
                onCheckedChange={(c) => setTerms({ ...terms, duree_indeterminee: !!c, duree: c ? "" : terms.duree })}
                data-testid="wizard-checkbox-openended" />
              <Label htmlFor="duree_indeterminee" className="font-normal cursor-pointer">Location à durée indéterminée</Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="resiliation" checked={terms.resiliation}
                onCheckedChange={(c) => setTerms({ ...terms, resiliation: !!c })}
                data-testid="wizard-checkbox-resiliation" />
              <Label htmlFor="resiliation" className="font-normal cursor-pointer">Résiliation d'un ancien contrat (ajoute une clause de résiliation)</Label>
            </div>
          </div>

          <div className="max-w-xl mx-auto mt-6 bg-slate-50 border border-slate-200 rounded-xl p-5" data-testid="wizard-recap">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-3">Récapitulatif avant génération</p>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <div className="flex justify-between gap-2"><dt className="text-slate-500">Type</dt><dd className="font-semibold text-slate-900">{type === "professionnel" ? "Professionnel" : "Personnel"}</dd></div>
              <div className="flex justify-between gap-2"><dt className="text-slate-500">Propriétaire</dt><dd className="font-semibold text-slate-900" data-testid="wizard-recap-owner">{selectedOwner ? `${selectedOwner.prenom} ${selectedOwner.nom.toUpperCase()}` : "—"}</dd></div>
              <div className="flex justify-between gap-2"><dt className="text-slate-500">Client</dt><dd className="font-semibold text-slate-900">{form.prenom} {form.nom.toUpperCase()}</dd></div>
              <div className="flex justify-between gap-2"><dt className="text-slate-500">Profession</dt><dd className="font-semibold text-slate-900">{form.profession || "—"}</dd></div>
              <div className="flex justify-between gap-2"><dt className="text-slate-500">Montant</dt><dd className="font-semibold text-slate-900">{terms.montant ? `${terms.montant} FCFA` : "—"}</dd></div>
              <div className="flex justify-between gap-2"><dt className="text-slate-500">Caution</dt><dd className="font-semibold text-slate-900">{terms.caution ? `${terms.caution} FCFA` : "2 mois de loyer"}</dd></div>
              <div className="flex justify-between gap-2"><dt className="text-slate-500">Durée</dt><dd className="font-semibold text-slate-900">{terms.duree_indeterminee ? "Indéterminée" : terms.duree || "—"}</dd></div>
              <div className="flex justify-between gap-2"><dt className="text-slate-500">Date d'effet</dt><dd className="font-semibold text-slate-900">{terms.date_effet || "—"}</dd></div>
              <div className="flex justify-between gap-2 sm:col-span-2"><dt className="text-slate-500">Bien loué</dt><dd className="font-semibold text-slate-900 text-right">{terms.designation || "—"}</dd></div>
              {terms.resiliation && <div className="flex justify-between gap-2 sm:col-span-2"><dt className="text-slate-500">Clause</dt><dd className="font-semibold text-amber-700">Résiliation de l'ancien contrat</dd></div>}
            </dl>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-between">
            <Button variant="outline" onClick={() => setStep(3)} data-testid="wizard-back-to-form-btn">
              <ArrowLeft className="h-4 w-4 mr-2" /> Modifier le client
            </Button>
            <Button onClick={generate} disabled={generating || !ownerId} data-testid="wizard-generate-contract-btn">
              {generating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileText className="h-4 w-4 mr-2" />}
              {generating ? "Génération en cours…" : "Générer le contrat"}
            </Button>
          </div>
        </div>
      )}

      <Dialog open={ownerDialog} onOpenChange={setOwnerDialog}>
        <DialogContent className="max-w-2xl" data-testid="wizard-owner-dialog">
          <DialogHeader>
            <DialogTitle>Nouveau propriétaire</DialogTitle>
          </DialogHeader>
          <OwnerForm value={ownerForm} onChange={setOwnerForm} prefix="wizard-owner-input" />
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={() => setOwnerDialog(false)} data-testid="wizard-owner-cancel-btn">Annuler</Button>
            <Button onClick={createOwner} disabled={ownerSaving || !ownerForm.nom.trim() || !ownerForm.prenom.trim()} data-testid="wizard-owner-save-btn">
              {ownerSaving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Créer et sélectionner
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {step === 5 && contract && (
        <div className="space-y-5" data-testid="wizard-step-result">
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-5 py-4 flex items-center gap-3">
            <CheckCircle2 className="h-6 w-6 text-emerald-600 shrink-0" />
            <div>
              <p className="text-sm font-bold text-emerald-800" data-testid="wizard-success-message">Contrat {contract.type === "professionnel" ? "professionnel" : "personnel"} créé et enregistré avec succès</p>
              <p className="text-xs text-emerald-700">{contract.numero_contrat} — {contract.filename_base} — visible immédiatement dans « Anciens contrats ».</p>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <a href={fileUrl("pdf")} download data-testid="wizard-download-pdf-btn"
              className={`inline-flex items-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors duration-150 ${
                contract.pdf_file ? "bg-slate-900 text-white hover:bg-slate-800" : "bg-slate-200 text-slate-400 pointer-events-none"
              }`}>
              <Download className="h-4 w-4" /> Télécharger PDF
            </a>
            <a href={fileUrl("docx")} download data-testid="wizard-download-docx-btn"
              className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-lg bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors duration-150">
              <Download className="h-4 w-4" /> Télécharger Word
            </a>
            <Button variant="outline" onClick={printContract} disabled={!contract.pdf_file} data-testid="wizard-print-btn">
              <Printer className="h-4 w-4 mr-2" /> Imprimer
            </Button>
            <Button variant="ghost" onClick={reset} data-testid="wizard-new-contract-btn">
              <RotateCcw className="h-4 w-4 mr-2" /> Nouveau contrat
            </Button>
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden" data-testid="wizard-contract-preview">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
              <FileText className="h-4 w-4 text-slate-500" />
              <span className="text-sm font-semibold text-slate-700">Aperçu du contrat final</span>
            </div>
            {contract.pdf_file && canEmbedPdf() ? (
              <iframe src={fileUrl("pdf")} title="Aperçu du contrat" className="w-full h-[70vh] bg-slate-100" data-testid="wizard-preview-iframe" />
            ) : contract.pdf_file ? (
              <div className="p-10 text-center space-y-4" data-testid="wizard-preview-fallback">
                <p className="text-sm text-slate-500">L'aperçu intégré n'est pas disponible sur cet appareil.</p>
                <a href={fileUrl("pdf")} target="_blank" rel="noopener noreferrer" data-testid="wizard-open-pdf-btn"
                  className="inline-flex items-center gap-2 bg-slate-900 text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:bg-slate-800 transition-colors duration-150">
                  <FileText className="h-4 w-4" /> Ouvrir le contrat (PDF)
                </a>
              </div>
            ) : (
              <p className="p-8 text-sm text-slate-500 text-center">
                Aperçu PDF indisponible. Téléchargez le fichier Word pour consulter le contrat.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
